import React, { useState, useEffect } from 'react';
import { X, Cookie, ChevronDown, ChevronUp, Check, Settings } from 'lucide-react';
import { useCookieConsent, COOKIE_CATEGORIES } from '../contexts/CookieConsentContext';
import { Link } from 'react-router-dom';

// Sleek Cookie Banner Component - glassmorphic design with adaptive text
export const CookieBanner = () => {
  const { 
    showBanner, 
    acceptAll, 
    rejectAll, 
    openPreferences 
  } = useCookieConsent();
  
  if (!showBanner) return null;

  // The public site is entirely light-themed (CP1 swept every page, incl. landing +
  // blog, to the light aurora), so the banner always uses the LIGHT treatment — a
  // readable frosted-white card with dark ink text, matching the site's frosted-card
  // language. Replaces a stale isLightPage allowlist that predated the light flip and
  // rendered white text on the light aurora on Home/Pricing/About/Blog/etc. (CP7.2).
  return (
    <div
      className="fixed bottom-20 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-[9999]"
      data-testid="cookie-banner"
    >
      <div
        className="backdrop-blur-2xl rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(255, 255, 255, 0.85)',
          border: '1px solid rgba(255, 255, 255, 0.65)',
          boxShadow: '0 20px 50px rgba(31, 38, 135, 0.12)'
        }}
      >
        <div className="p-4">
          {/* Compact Header */}
          <div className="flex items-center gap-3 mb-3">
            <div className="p-1.5 rounded-lg" style={{ background: 'rgba(255, 92, 92, 0.12)' }}>
              <Cookie className="w-4 h-4 text-[#FF5C5C]" />
            </div>
            <p className="text-sm flex-1" style={{ color: 'rgba(20, 20, 43, 0.75)' }}>
              We use cookies to enhance your experience.{' '}
              <Link to="/privacy-policy" className="text-[#FF5C5C] hover:underline">
                Read our privacy policy
              </Link>
            </p>
          </div>

          {/* Compact Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={acceptAll}
              className="flex-1 px-4 py-2 bg-[#FF5C5C] text-white text-sm font-medium rounded-lg hover:bg-[#FF4545] transition-all duration-200"
              data-testid="cookie-accept-all"
            >
              Accept All
            </button>
            <button
              onClick={rejectAll}
              className="flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 border bg-[#14142b]/[0.05] text-[#14142b] hover:bg-[#14142b]/10 border-[#14142b]/15"
              data-testid="cookie-reject-all"
            >
              Reject All
            </button>
            <button
              onClick={openPreferences}
              className="p-2 rounded-lg transition-all duration-200 text-[#14142b]/60 hover:text-[#14142b] hover:bg-[#14142b]/10"
              data-testid="cookie-customize"
              title="Customize preferences"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sleek Cookie Preferences Modal - glassmorphic design
export const CookiePreferencesModal = () => {
  const { 
    showPreferences, 
    closePreferences, 
    savePreferences, 
    consent,
    acceptAll
  } = useCookieConsent();

  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  const [expandedCategory, setExpandedCategory] = useState(null);

  // Sync preferences with current consent when modal opens
  useEffect(() => {
    if (showPreferences && consent) {
      setPreferences({
        essential: true,
        analytics: consent.analytics || false,
        marketing: consent.marketing || false,
      });
    }
  }, [showPreferences, consent]);

  if (!showPreferences) return null;

  const toggleCategory = (categoryId) => {
    if (categoryId === 'essential') return;
    setPreferences(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const toggleExpand = (categoryId) => {
    setExpandedCategory(prev => prev === categoryId ? null : categoryId);
  };

  const handleSave = () => {
    savePreferences(preferences);
  };

  const categories = [
    {
      ...COOKIE_CATEGORIES.essential,
      cookies: [
        { name: 'Session', purpose: 'Maintains session state', duration: 'Session' },
        { name: 'CSRF', purpose: 'Security protection', duration: 'Session' },
      ],
    },
    {
      ...COOKIE_CATEGORIES.analytics,
      cookies: [
        { name: '_ga', purpose: 'Google Analytics', duration: '2 years' },
        { name: '_gid', purpose: 'User distinction', duration: '24 hours' },
      ],
    },
    {
      ...COOKIE_CATEGORIES.marketing,
      cookies: [
        { name: '_fbp', purpose: 'Meta Pixel tracking', duration: '3 months' },
      ],
    },
  ];

  return (
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      data-testid="cookie-preferences-modal"
    >
      {/* Backdrop (dark scrim stays — standard modal behavior) */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={closePreferences}
      />

      {/* Modal — light frosted card (CP7.2: was dark; the site is fully light) */}
      <div
        className="relative w-full max-w-lg rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.7)',
          boxShadow: '0 25px 50px rgba(31, 38, 135, 0.18)'
        }}
      >
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(20, 20, 43, 0.1)' }}>
          <div className="flex items-center gap-3">
            <div className="p-1.5 rounded-lg" style={{ background: 'rgba(255, 92, 92, 0.12)' }}>
              <Cookie className="w-4 h-4 text-[#FF5C5C]" />
            </div>
            <h2 className="text-base font-semibold" style={{ color: '#14142b' }}>Cookie Preferences</h2>
          </div>
          <button
            onClick={closePreferences}
            className="p-1.5 rounded-lg transition-colors text-[#14142b]/40 hover:text-[#14142b] hover:bg-[#14142b]/10"
            data-testid="cookie-close-modal"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 max-h-[50vh] overflow-y-auto">
          <p className="text-sm mb-4" style={{ color: 'rgba(20, 20, 43, 0.6)' }}>
            Manage your preferences. Essential cookies cannot be disabled.{' '}
            <Link to="/privacy-policy" className="text-[#FF5C5C] hover:underline" onClick={closePreferences}>
              Privacy Policy
            </Link>
          </p>

          {/* Cookie Categories */}
          <div className="space-y-2">
            {categories.map((category) => (
              <div
                key={category.id}
                className="rounded-xl overflow-hidden"
                style={{ background: 'rgba(255, 255, 255, 0.55)', border: '1px solid rgba(20, 20, 43, 0.1)' }}
              >
                {/* Category Header */}
                <div className="px-4 py-3 flex items-center justify-between">
                  <button
                    className="flex items-center gap-2 flex-1 text-left"
                    onClick={() => toggleExpand(category.id)}
                  >
                    {expandedCategory === category.id ? (
                      <ChevronUp className="w-4 h-4" style={{ color: 'rgba(20, 20, 43, 0.4)' }} />
                    ) : (
                      <ChevronDown className="w-4 h-4" style={{ color: 'rgba(20, 20, 43, 0.4)' }} />
                    )}
                    <div>
                      <h3 className="text-sm font-medium" style={{ color: '#14142b' }}>{category.name}</h3>
                      <p className="text-xs" style={{ color: 'rgba(20, 20, 43, 0.5)' }}>{category.description}</p>
                    </div>
                  </button>

                  {/* Toggle Switch */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    disabled={category.required}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      preferences[category.id]
                        ? 'bg-[#FF5C5C]'
                        : 'bg-[#14142b]/20'
                    } ${category.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`cookie-toggle-${category.id}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                        preferences[category.id] ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Category Details (Expanded) */}
                {expandedCategory === category.id && (
                  <div className="px-4 pb-3 pt-1" style={{ borderTop: '1px solid rgba(20, 20, 43, 0.08)' }}>
                    <div className="space-y-1.5">
                      {category.cookies.map((cookie, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="font-mono" style={{ color: 'rgba(20, 20, 43, 0.7)' }}>{cookie.name}</span>
                          <span style={{ color: 'rgba(20, 20, 43, 0.45)' }}>{cookie.duration}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 flex gap-3" style={{ borderTop: '1px solid rgba(20, 20, 43, 0.1)' }}>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2.5 bg-[#FF5C5C] text-white text-sm font-medium rounded-lg hover:bg-[#FF4545] transition-all duration-200 flex items-center justify-center gap-2"
            data-testid="cookie-save-preferences"
          >
            <Check className="w-4 h-4" />
            Save
          </button>
          <button
            onClick={acceptAll}
            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 border bg-[#14142b]/[0.05] text-[#14142b] hover:bg-[#14142b]/10 border-[#14142b]/15"
            data-testid="cookie-accept-all-modal"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

// Cookie settings button for footer
export const CookieSettingsButton = ({ className = '' }) => {
  const { openPreferences } = useCookieConsent();

  return (
    <button
      onClick={openPreferences}
      className={`text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1 ${className}`}
      data-testid="cookie-settings-button"
    >
      <Cookie className="w-4 h-4" />
      Cookie Settings
    </button>
  );
};

export default CookieBanner;
